<script setup lang="ts">
// #737: 料金セクション（/ 用）。
// 「冒険者/探究者/支援者」の3列プラン比較マトリクスを廃し、直前の「利用の流れ」
// （遊ぶ/つくる/学ぶ）に対応した縦構造にする。基本無料（冒険者プラン ¥0）を全幅で
// 宣言し、有料商品＝エンコーディア探索の紙の魔導書（エリア買い切り）を3段はしごで
// 透明に見せる。設計・コピー・価格表記の定石は Fable レビュー（#737）に基づく。
//   - リード「まずは無料で…／写して学ぶ紙の魔導書は買い切りで」。将来サブスク（MP増量の
//     上位プラン）を出す方針のため「永久無料」に読める表現は避ける（脚注で軽く登場予定を匂わせる）。
//   - Coming Soon の上位プランは列を作らず脚注1行。名前・価格は出さない（内容未確定・UGC充足待ち）。
//   - MP を使う操作には壺アイコンを添える。AIサポート・作品の改造/作成/公開(クリスタル化)は
//     いずれも MP を消費する（円コストは無料だが「無料」と断定せず壺で示す）。
import { mdiGamepadVariant, mdiFeather, mdiCompassOutline, mdiWaterCircle } from '@mdi/js'

// エンコーディア探索＝紙の魔導書のエリア買い切り（写経教材）。価格は product_master 準拠:
// はじまりの港=¥0(全1冊) / まどろみの浜辺=¥1,100(全1冊) / クリスタルウェーブ島〜=¥3,300(全3冊)。
const areaLadder = [
  { name: 'はじまりの港', price: '無料', free: true },
  { name: 'まどろみの浜辺', price: '1,100円', free: false },
  { name: 'クリスタルウェーブ島〜', price: '3,300円', unit: ' / エリア', free: false },
]
</script>

<template>
  <div class="centered-area">
    <div class="pricing-card">
      <h2 class="pricing-title">
        料金
      </h2>
      <p class="pricing-lead">
        まずは無料で、あそんで、つくって。<br>
        写して学ぶ<strong>紙の魔導書</strong>は、買い切りで。
      </p>

      <!-- 基本プラン（全幅ミント）: 利用の流れ STEP1 と同形 -->
      <div class="plan-hero">
        <div class="plan-hero-head">
          <span class="plan-hero-name">冒険者プラン</span>
          <span class="plan-hero-price">
            <span class="yen">¥0</span>
            <span class="plan-hero-free">まずは無料</span>
          </span>
        </div>
        <p class="plan-hero-sub">
          メールアドレスだけで登録。クレジットカードは不要です。
        </p>
        <div class="plan-hero-includes">
          <span class="include-chip">
            <v-icon
              :icon="mdiWaterCircle"
              color="purple"
              size="small"
              class="mp-inline"
            />
            魔導エネルギー 200
            <small>毎月満タンまで回復</small>
          </span>
          <span class="include-chip">
            <v-icon
              :icon="mdiWaterCircle"
              color="purple"
              size="small"
              class="mp-inline"
            />
            AIサポート
            <small>女神・先輩・ヒントン</small>
          </span>
        </div>
      </div>

      <!-- 並列3カード（遊ぶ→つくる→学ぶ）: 利用の流れの3ルートと同形・同順 -->
      <div class="plan-paths">
        <!-- 遊ぶ -->
        <div class="plan-card">
          <v-icon
            :icon="mdiGamepadVariant"
            size="36"
            class="plan-card-icon"
          />
          <h3 class="plan-card-title">
            みんなの作品で、遊ぶ
          </h3>
          <div class="plan-card-place">
            魔法研究会
          </div>
          <div class="plan-card-price">
            無料
          </div>
          <p class="plan-card-desc">
            集まった作品をブラウザですぐプレイ。デコード＆リミックス（作品の改造）は
            <v-icon
              :icon="mdiWaterCircle"
              color="purple"
              size="x-small"
              class="mp-inline"
            />
            を使います。
          </p>
        </div>

        <!-- つくる -->
        <div class="plan-card">
          <v-icon
            :icon="mdiFeather"
            size="36"
            class="plan-card-icon"
          />
          <h3 class="plan-card-title">
            作って、公開する
          </h3>
          <div class="plan-card-place">
            羽根筆のゆりかご
          </div>
          <div class="plan-card-price">
            無料
          </div>
          <p class="plan-card-desc">
            思いついたゲームを自由に創作し、魔法研究会へ公開。作成も公開（クリスタル化）も
            <v-icon
              :icon="mdiWaterCircle"
              color="purple"
              size="x-small"
              class="mp-inline"
            />
            を使います。
          </p>
        </div>

        <!-- 学ぶ（唯一の有料＝紙の魔導書のエリア買い切り） -->
        <div class="plan-card plan-card--paid">
          <v-icon
            :icon="mdiCompassOutline"
            size="36"
            class="plan-card-icon"
          />
          <h3 class="plan-card-title">
            写して、学ぶ
          </h3>
          <div class="plan-card-place">
            エンコーディア探索
          </div>
          <div class="plan-card-price plan-card-price--intro">
            最初のエリアは<span class="free-em">無料</span>
          </div>
          <dl class="area-ladder">
            <div
              v-for="a in areaLadder"
              :key="a.name"
              class="area-row"
            >
              <dt>{{ a.name }}</dt>
              <dd :class="{ 'is-free': a.free }">
                {{ a.price }}<small v-if="a.unit">{{ a.unit }}</small>
              </dd>
            </div>
          </dl>
          <p class="plan-card-note">
            紙の魔導書のエリア買い切り（送料込み）。<br>
            匿名配送の
            <a
              href="https://codeandmagic.booth.pm/"
              target="_blank"
              rel="noopener noreferrer"
            >BOOTH店</a>
            もご利用いただけます。
          </p>
        </div>
      </div>

      <!-- 脚注: MP解毒 + Coming Soon の匂わせ（価格は書かない） -->
      <p class="plan-footnote">
        <v-icon
          :icon="mdiWaterCircle"
          color="purple"
          size="x-small"
          class="mp-inline"
        />
        は魔導エネルギー（MP）。作品の改造・作成や AIサポートに使います。MPは毎月満タンまで回復します。<br>
        もっとたっぷり楽しみたい人向けの上位プランも、登場予定です。
      </p>

      <p class="pricing-detail-link">
        <NuxtLink to="/pricing">
          料金について詳しく見る &rarr;
        </NuxtLink>
      </p>

      <SignUpNow />
    </div>
  </div>
</template>

<style scoped>
.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
}

.pricing-card {
  max-width: 1080px;
  margin: 0 auto;
  background: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
  padding: clamp(26px, 4vw, 48px) clamp(20px, 4vw, 48px);
  color: var(--landing-color-text-dark);
}

.pricing-title {
  font-size: var(--landing-h2-size);
  font-weight: 700;
  text-align: center;
  margin: 0 0 clamp(14px, 2vw, 20px);
  color: var(--landing-color-navy);
}

.pricing-lead {
  text-align: center;
  font-size: clamp(1.02rem, 1.5vw, 1.2rem);
  font-weight: 600;
  line-height: 1.8;
  color: var(--landing-color-teal-darker);
  margin: 0 0 clamp(24px, 4vw, 34px);
}

.pricing-lead strong {
  /* 紙の魔導書＝唯一の商品を強調 */
  border-bottom: 2px solid var(--landing-color-teal);
  padding-bottom: 1px;
}

/* ===== 基本プラン（全幅ミント） ===== */
.plan-hero {
  background: var(--landing-color-card-mint);
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  padding: clamp(18px, 2.6vw, 26px);
  margin-bottom: clamp(16px, 3vw, 22px);
}

.plan-hero-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 16px;
}

.plan-hero-name {
  font-family: 'Noto Sans JP', sans-serif;
  font-size: clamp(1.15rem, 1.8vw, 1.4rem);
  font-weight: 700;
}

.plan-hero-price {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
}

.plan-hero-price .yen {
  font-size: clamp(1.6rem, 2.6vw, 2.1rem);
  font-weight: 800;
  color: var(--landing-color-teal-darker);
  line-height: 1;
}

.plan-hero-free {
  font-size: 0.72em;
  font-weight: 800;
  color: #fff;
  background: var(--landing-color-teal-darker);
  border-radius: 999px;
  padding: 2px 12px;
  white-space: nowrap;
}

.plan-hero-sub {
  margin: 8px 0 0;
  font-size: 0.95rem;
  color: #4a4a4a;
}

.plan-hero-includes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: 14px;
}

.include-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--landing-color-border-mint);
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 700;
  font-size: 0.9rem;
}

.include-chip small {
  font-weight: 400;
  color: #6a6a6a;
}

/* ===== 並列3カード ===== */
.plan-paths {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.plan-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  padding: clamp(18px, 2.6vw, 24px);
  text-align: center;
}

/* 唯一の有料カード（学ぶ）はティール枠＋影で視線を集める */
.plan-card--paid {
  border-color: var(--landing-color-teal);
  box-shadow: var(--landing-shadow-light);
}

.plan-card-icon {
  color: var(--landing-color-teal);
}

.plan-card-title {
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 2px 0 0;
}

.plan-card-place {
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  /* 白文字 AA (≥4.5) を満たす濃ティール（#737 Lighthouse） */
  background: var(--landing-color-teal-darker);
  border-radius: 999px;
  padding: 2px 12px;
}

.plan-card-price {
  font-family: 'Noto Sans JP', sans-serif;
  font-size: clamp(1.3rem, 2.2vw, 1.7rem);
  font-weight: 800;
  color: var(--landing-color-teal-darker);
  margin-top: 6px;
}

/* 「最初のエリアは無料」: 接頭辞を小さく、無料を大きく */
.plan-card-price--intro {
  font-size: clamp(0.92rem, 1.5vw, 1.1rem);
  font-weight: 700;
}

.plan-card-price--intro .free-em {
  font-size: 1.55em;
  font-weight: 800;
  margin-left: 4px;
}

.plan-card-desc {
  margin: 4px 0 0;
  font-size: 0.9rem;
  line-height: 1.7;
  color: #4a4a4a;
}

.mp-inline {
  vertical-align: -0.15em;
}

/* エリア3段はしご（学ぶカード内） */
.area-ladder {
  width: 100%;
  margin: 10px 0 0;
  padding: 0;
}

.area-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 2px;
  border-bottom: 1px dashed var(--landing-color-border-mint);
}

.area-row:last-child {
  border-bottom: none;
}

.area-row dt {
  font-size: 0.9rem;
  text-align: left;
  color: var(--landing-color-text-dark);
}

.area-row dd {
  margin: 0;
  font-weight: 800;
  font-size: 1rem;
  white-space: nowrap;
  color: var(--landing-color-teal-darker);
}

.area-row dd small {
  font-weight: 400;
  font-size: 0.72em;
  color: #6a6a6a;
}

.plan-card-note {
  margin: 12px 0 0;
  font-size: 0.8rem;
  line-height: 1.7;
  color: #6a6a6a;
}

.plan-card-note a {
  color: #1565c0;
  font-weight: 700;
  text-decoration: none;
}

.plan-card-note a:hover {
  text-decoration: underline;
}

/* ===== 脚注・導線 ===== */
.plan-footnote {
  margin: clamp(18px, 3vw, 24px) 0 0;
  font-size: 0.82rem;
  line-height: 1.8;
  color: #6a6a6a;
  text-align: center;
}

.pricing-detail-link {
  text-align: center;
  margin: 14px 0;
}

.pricing-detail-link a {
  color: var(--landing-color-teal-darker);
  font-weight: 700;
  text-decoration: none;
}

.pricing-detail-link a:hover {
  text-decoration: underline;
}

@media (min-width: 600.02px) {
  .plan-paths {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    align-items: start;
  }
}
</style>
